/* ============================================================
   LKZJ card enhancement
   - <lkzj-card> auto-cycles product images while hovered
   - eye button opens a shared quick-view modal
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Card custom element -----------------------------
     The horizontal scroll itself is pure CSS (driven by :hover on the
     card). This element only marks the host elements and wires up the
     quick-view eye button. -------------------------------------- */
  if (!customElements.get('lkzj-card')) {
    customElements.define(
      'lkzj-card',
      class LkzjCard extends HTMLElement {
        connectedCallback() {
          // Mark the media box (to hide the theme's own image) and the card
          // root (the hover target — Shrine overlays a full-card link on the
          // media, so .card__media itself never receives :hover / mouseenter).
          var mediaBox = this.closest('.card__media');
          if (mediaBox) mediaBox.classList.add('lkzj-card-host');
          var cardRoot = this.closest('.card-wrapper') || this.closest('.card');
          if (cardRoot) cardRoot.classList.add('lkzj-card-host');

          // Step-on-hover slideshow: each time the mouse enters the card the
          // strip slides left by exactly one image and then holds. Leaving the
          // card keeps the current image; re-entering advances to the next.
          this.strip = this.querySelector('.lkzj-strip');
          if (this.strip) {
            var slides = this.querySelectorAll('.lkzj-slide').length;
            this.count = parseInt(this.style.getPropertyValue('--lkzj-n'), 10) || slides / 2 || 1;
            this.index = 0;
            this.animating = false;
            var hoverTarget = cardRoot || mediaBox || this;
            hoverTarget.addEventListener('mouseenter', this.step.bind(this));
            this.strip.addEventListener('transitionend', this.onTransitionEnd.bind(this));
          }

          var eye = this.querySelector('.lkzj-card__eye');
          if (eye) {
            eye.addEventListener('click', this.openQuickView.bind(this));
          }

          var add = this.querySelector('.lkzj-card__add');
          if (add) {
            add.addEventListener('click', this.addToCart.bind(this));
          }
        }

        addToCart(e) {
          e.preventDefault();
          e.stopPropagation();
          var btn = e.currentTarget;
          if (btn.disabled || btn.classList.contains('is-loading')) return;
          var vid = btn.getAttribute('data-vid');
          if (!vid) { window.location.href = btn.getAttribute('data-url') || '/cart'; return; }
          LkzjCart.add(vid, btn);
        }

        step() {
          if (!this.strip || this.animating || this.count < 2) return;
          this.animating = true;
          this.index += 1;
          this.strip.style.transition = '';
          this.strip.style.transform = 'translateX(' + -this.index * 100 + '%)';
        }

        onTransitionEnd() {
          this.animating = false;
          // reached the duplicated first image -> snap back to the real first
          // one (identical frame) so stepping can continue seamlessly.
          if (this.index >= this.count) {
            this.index = 0;
            this.strip.style.transition = 'none';
            this.strip.style.transform = 'translateX(0)';
            // force reflow, then restore the smooth transition for next step
            void this.strip.offsetWidth;
            this.strip.style.transition = '';
          }
        }

        openQuickView(e) {
          e.preventDefault();
          e.stopPropagation();
          var tpl = this.querySelector('.lkzj-card__qv-data');
          if (tpl) LkzjQuickView.open(tpl.innerHTML);
        }
      }
    );
  }

  /* ---------- Shared quick-view modal ------------------------- */
  var LkzjQuickView = {
    backdrop: null,

    build: function () {
      if (this.backdrop) return;
      var backdrop = document.createElement('div');
      backdrop.className = 'lkzj-qv-backdrop';
      backdrop.innerHTML =
        '<div class="lkzj-qv-modal" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="lkzj-qv-close" aria-label="Close">&times;</button>' +
        '<div class="lkzj-qv-body"></div>' +
        '</div>';
      document.body.appendChild(backdrop);

      var self = this;
      backdrop.addEventListener('click', function (ev) {
        if (ev.target === backdrop) self.close();
      });
      backdrop.querySelector('.lkzj-qv-close').addEventListener('click', function () {
        self.close();
      });
      document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape') self.close();
      });
      this.backdrop = backdrop;
    },

    open: function (html) {
      this.build();
      var body = this.backdrop.querySelector('.lkzj-qv-body');
      body.innerHTML = html;
      this.bindGallery(body);
      // force reflow then animate in
      this.backdrop.offsetHeight; // eslint-disable-line
      this.backdrop.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
    },

    close: function () {
      if (!this.backdrop) return;
      this.backdrop.classList.remove('is-open');
      document.documentElement.style.overflow = '';
    },

    bindGallery: function (root) {
      var mainImg = root.querySelector('.lkzj-qv__main img');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('.lkzj-qv__thumb'));
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-full');
          if (mainImg && src) mainImg.src = src;
          thumbs.forEach(function (t) {
            t.classList.toggle('is-active', t === thumb);
          });
        });
      });
    },
  };

  window.LkzjQuickView = LkzjQuickView;

  /* ---------- Shared cart helper (AJAX add + toast + counts) --- */
  var LkzjCart = {
    add: function (variantId, btn) {
      if (btn) btn.classList.add('is-loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (item) {
          if (btn) {
            btn.classList.remove('is-loading');
            btn.classList.add('is-done');
            setTimeout(function () { btn.classList.remove('is-done'); }, 1400);
          }
          LkzjCart.syncCount();
          LkzjCart.toast((item && item.product_title) || 'Item');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .catch(function () {
          if (btn) btn.classList.remove('is-loading');
          LkzjCart.toast('Could not add — tap to open product', btn && btn.getAttribute('data-url'));
        });
    },

    syncCount: function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var n = cart.item_count || 0;
          document.querySelectorAll('.cart-count-bubble, [data-lkzj-cart-count]').forEach(function (el) {
            var span = el.querySelector('span:not([aria-hidden])') || el;
            if (el.hasAttribute('data-lkzj-cart-count')) {
              el.textContent = n;
              el.classList.toggle('is-empty', n === 0);
            } else {
              var t = el.querySelector('span[aria-hidden="true"]');
              if (t) t.textContent = n;
            }
          });
        });
    },

    toast: function (title, url) {
      var t = document.querySelector('.lkzj-toast');
      if (!t) {
        t = document.createElement('div');
        t.className = 'lkzj-toast';
        document.body.appendChild(t);
      }
      var link = url || '/cart';
      var label = url ? 'Open' : 'View cart';
      t.innerHTML = '<span>✓ Added: ' + String(title).slice(0, 32) + '</span><a href="' + link + '">' + label + '</a>';
      // reflow then show
      void t.offsetWidth;
      t.classList.add('is-show');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () { t.classList.remove('is-show'); }, 3200);
    },
  };

  window.LkzjCart = LkzjCart;
})();
